import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import ReviewSummary from '../ReviewSummary/ReviewSummary';
import LayoutBar from '../LayoutBar/LayoutBar';
import LocalStorageWrapper from '../../services/LocalStorageWrapper/LocalStorageWrapper';
import UserModel from '../../types/UserModel';
import Constants, {DashboardFilter, DashboardViewLayout, ModalStates, SortMetric} from '../../globals/Constants';
import UrlParser from '../../utils/UrlParser/UrlParser';
import Strings from '../../globals/Strings';
import {MyWindow} from '../../types/MyWindow';
import AuthorizedActionsModel from '../../types/AuthorizedActionsModel';
import ReviewModel from '../../types/ReviewModel';
import DashboardEmptyStateContainer from '../DashboardEmptyState/DashboardEmptyStateContainer';
import EmptyStateContainer from '../EmptyState/EmptyStateContainer';
import HotjarService from '../../services/HotjarService/HotjarService';
import Themeable from '../../types/Themeable';
import ReviewerModel from '../../types/ReviewerModel';
import LoadRemainingReviewsButtonContainer from '../LoadRemainingReviewsButton/LoadRemainingReviewsButtonContainer';
import LoadRemainingReviewsText from '../LoadRemainingReviewsText/LoadRemainingReviewsText';
import RetirementBar from '../RetirementBar/RetirementBar';

declare const window: MyWindow;

export default class DashboardReviews extends React.Component<DashboardReviewsProps, DashboardReviewsState> {
   readonly state: DashboardReviewsState = {viewLayout: DashboardViewLayout[LocalStorageWrapper.getItem(Constants.localStorageKeys.dashboardLayout) as keyof typeof DashboardViewLayout] || DashboardViewLayout.grid};

   constructor(props: DashboardReviewsProps) {
      super(props);
      autoBind(this);

      const {authorizedActions, currentUser, setModalState} = props;
      const userCanStartTrial = authorizedActions.canStartTrial;
      const addUserToTrial = UrlParser.getUrlQueryParam(window.location.search, Constants.splashStartTrialQueryParamString) === 'true';

      if (userCanStartTrial && addUserToTrial && currentUser.userId !== '') {
         setModalState(ModalStates.trialOnly);
      }
   }

   componentDidMount() {
      const {isReviewsAtLimit, isTrialActive, currentUser} = this.props;
      HotjarService.dashboardViewed(isReviewsAtLimit, isTrialActive, currentUser.userId);
   }

   setViewLayout(viewLayout: DashboardViewLayout): void {
      this.setState({viewLayout});
   }

   getEmptyState(): JSX.Element {
      const {reviews, dashboardFilter, searchText} = this.props;

      if (reviews?.length !== 0) {
         return null;
      }

      const emptyState = <DashboardEmptyStateContainer/>;
      const sharedEmptyState = <EmptyStateContainer title={Strings.emptyStates.dashboard.noSharedTitle} text={Strings.emptyStates.dashboard.noSharedMessage} maxWidth={Constants.dashboardEmptyStateMaxWidth} />;
      const searchEmptyState = <EmptyStateContainer title={Strings.labels.noResultsFound} maxWidth={Constants.dashboardEmptyStateMaxWidth} />;

      if (reviews?.length === 0 && !searchText) {
         return dashboardFilter === DashboardFilter.others ? sharedEmptyState : emptyState;
      }

      if (searchText !== '') {
         return searchEmptyState;
      }

      return emptyState;
   }

   setReviewsSortMetric(reviewsSortMetric: SortMetric) {
      const {currentUser, setReviewsSortMetric} = this.props;
      const localStorageKey = LocalStorageWrapper.getKey(Constants.localStorageKeys.reviewsSortMetric, currentUser.userId);
      LocalStorageWrapper.setItem(localStorageKey, `${reviewsSortMetric}`);
      setReviewsSortMetric(reviewsSortMetric);
   }

   getReviewCountByFilter() {
      const {dashboardFilter, totalReviewsCount, totalOwnedReviewsCount, totalNotOwnedReviewsCount} = this.props;
      if (dashboardFilter === DashboardFilter.all) {
         return totalReviewsCount;
      } else if (dashboardFilter === DashboardFilter.owned) {
         return totalOwnedReviewsCount;
      }

      return totalNotOwnedReviewsCount;
   }

   render() {
      const {currentUser, reviews, themeName, searchText, setSearchText, reviewsSortMetric} = this.props;
      const {viewLayout} = this.state;
      const layoutClass = viewLayout === DashboardViewLayout.grid ? 'grid-layout' : 'list-layout';
      const reviewsClassList = classNames('reviews', viewLayout === DashboardViewLayout.grid ? 'row' : 'column');
      const dashboardClassList = classNames('dashboard', layoutClass);
      const emptyState = this.getEmptyState();
      const totalReviewCountByFilter = this.getReviewCountByFilter();

      return (
         <div className={dashboardClassList}>
            <div className="dashboard-inner-container">
               <LayoutBar themeName={themeName} viewLayout={viewLayout} setViewLayout={this.setViewLayout} setSearchText={setSearchText} searchText={searchText} reviewsSortMetric={reviewsSortMetric} setReviewsSortMetric={this.setReviewsSortMetric} />
               {emptyState || (
                  <div className="reviews-container">
                     <RetirementBar />
                     <ol className={reviewsClassList}>
                        {reviews?.map(review => {
                           const reviewVersion = review.ReviewVersions.filter(ver => !ver.IsDeleted).pop();
                           const reviewOwner = review.Reviewers.filter(r => r.Role === Constants.reviewerRoles.owner)[0] || {} as ReviewerModel;
                           return <ReviewSummary
                              themeName={themeName}
                              title={review.Title}
                              description={reviewVersion.Description}
                              key={review.Id}
                              id={review.Id}
                              latestVersionNumber={reviewVersion.VersionNumber}
                              isOwner={currentUser.userId && reviewOwner.Id === currentUser.userId}
                              viewLayout={viewLayout}
                              thumbnailUrl={reviewVersion.ThumbnailUrl}
                              isInactive={review.IsInactive}
                              mediaContentType={reviewVersion.MediaContentType}
                              hasComments={review.HasComments}
                              createdDate={new Date(reviewVersion.CreatedDate)}
                              mediaDuration={reviewVersion.MediaDuration}
                              reviewOwner={reviewOwner}
                           />;
                        })}
                     </ol>
                     {reviews?.length < totalReviewCountByFilter && reviews?.length !== 0 && !searchText && <LoadRemainingReviewsText themeName={themeName} numberOfReviews={reviews.length} totalReviewsCount={totalReviewCountByFilter} />}
                     {reviews?.length < totalReviewCountByFilter && reviews?.length !== 0 && !searchText && <LoadRemainingReviewsButtonContainer />}
                  </div>
               )}
            </div>
         </div>
      );
   }
}

export interface DashboardReviewsProps extends Themeable {
   reviews: ReviewModel[];
   currentUser: UserModel;
   authorizedActions: AuthorizedActionsModel;
   searchText: string;
   dashboardFilter: DashboardFilter;
   isReviewsAtLimit: boolean;
   isTrialActive: boolean;
   reviewsSortMetric: SortMetric;
   totalReviewsCount: number;
   totalOwnedReviewsCount: number;
   totalNotOwnedReviewsCount: number;
   setReviewsSortMetric(reviewsSortMetric: SortMetric): void;
   setModalState(modalState: ModalStates): void;
   setSearchText(searchText: string): void;
}

export interface DashboardReviewsState {
   viewLayout: DashboardViewLayout;
}
